import { pick } from 'lodash';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { FirebaseApi, SharetribeApi } from '../../util/api';
import { LISTING_ID, IMAGE_FIELDS, POST_EDIT } from '../../util/constants';
import { getSuppliersWhoSentEstimation } from '../../util/dataExtractors';
import { StatusCode } from '../../util/enums';
import { storableError } from '../../util/errors';
import { getImageVariantConfigs } from '../../util/helperFunctions';
import { types as sdkTypes } from '../../util/sdkLoader';

const { UUID } = sdkTypes;

// ================ Action types ================ //
export const SHOW_LISTING_REQUEST = 'app/CustomerCostEstimationToolPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/CustomerCostEstimationToolPage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/CustomerCostEstimationToolPage/SHOW_LISTING_ERROR';
export const FETCH_COST_ESTIMATION_DOCS_REQUEST =
  'app/CustomerCostEstimationToolPage/FETCH_COST_ESTIMATION_DOCS_REQUEST';
export const FETCH_COST_ESTIMATION_DOCS_SUCCESS =
  'app/CustomerCostEstimationToolPage/FETCH_COST_ESTIMATION_DOCS_SUCCESS';
export const FETCH_COST_ESTIMATION_DOCS_ERROR =
  'app/CustomerCostEstimationToolPage/FETCH_COST_ESTIMATION_DOCS_ERROR';
export const SET_INITIAL_VALUES = 'app/CustomerCostEstimationToolPage/SET_INITIAL_VALUES';

export const SAVE_COST_ESTIMATION_REQUEST =
  'app/CostEstimationDetailsPage/SAVE_COST_ESTIMATION_REQUEST';
export const SAVE_COST_ESTIMATION_SUCCESS =
  'app/CostEstimationDetailsPage/SAVE_COST_ESTIMATION_SUCCESS';
export const SAVE_COST_ESTIMATION_ERROR =
  'app/CostEstimationDetailsPage/SAVE_COST_ESTIMATION_ERROR';

const initialState = {
  showListingInProgress: false,
  showListingError: null,
  listingAuthorId: null,
  costEstimationDocuments: [],
  fetchCostEstimationDocumentsError: null,
  fetchCostEstimationDocumentsInProgress: false,
  saveCostEstimationInProgress: false,
  saveCostEstimationError: null,
};

// ================ Reducer ================ //

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };
    case SHOW_LISTING_REQUEST:
      return { ...state, showListingInProgress: true, showListingError: null };
    case SHOW_LISTING_SUCCESS:
      return {
        ...state,
        showListingInProgress: false,
        listingAuthorId: payload.listingAuthorId,
        listingTitle: payload.listingTitle,
        showListingError: null,
      };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingInProgress: false, showListingError: payload };
    case FETCH_COST_ESTIMATION_DOCS_REQUEST:
      return {
        ...state,
        fetchCostEstimationDocumentsInProgress: true,
        fetchCostEstimationDocumentsError: null,
        // Do not reset costEstimationDocuments here to preserve existing documents
      };
    case FETCH_COST_ESTIMATION_DOCS_SUCCESS:
      return {
        ...state,
        costEstimationDocuments: [...state.costEstimationDocuments, ...payload], // Append new documents
        fetchCostEstimationDocumentsInProgress: false,
        fetchCostEstimationDocumentsError: null,
      };
    case FETCH_COST_ESTIMATION_DOCS_ERROR:
      return {
        ...state,
        fetchCostEstimationDocumentsInProgress: false,
        fetchCostEstimationDocumentsError: payload,
        // Do not reset costEstimationDocuments on error to preserve existing documents
      };
    case SAVE_COST_ESTIMATION_REQUEST:
      return {
        ...state,
        saveCostEstimationInProgress: true,
        saveCostEstimationError: null,
      };
    case SAVE_COST_ESTIMATION_SUCCESS:
      return {
        ...state,
        saveCostEstimationInProgress: false,
        saveCostEstimationError: null,
      };
    case SAVE_COST_ESTIMATION_ERROR:
      return {
        ...state,
        saveCostEstimationInProgress: false,
        saveCostEstimationError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const listingAuthorIdSelector = state =>
  state.CustomerCostEstimationToolPage.listingAuthorId;
export const listingTitleSelector = state => state.CustomerCostEstimationToolPage.listingTitle;

export const costEstimationDocumentsSelector = state =>
  state.CustomerCostEstimationToolPage.costEstimationDocuments;

// ================ Action creators ================ //
export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = () => ({
  type: SHOW_LISTING_REQUEST,
});
export const showListingSuccess = (listingAuthorId, listingTitle) => ({
  type: SHOW_LISTING_SUCCESS,
  payload: { listingAuthorId, listingTitle },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});
export const saveCostEstimationRequest = () => ({
  type: SAVE_COST_ESTIMATION_REQUEST,
});
export const saveCostEstimationSuccess = () => ({
  type: SAVE_COST_ESTIMATION_SUCCESS,
});

export const saveCostEstimationError = e => ({
  type: SAVE_COST_ESTIMATION_ERROR,
  error: true,
  payload: e,
});

export const fetchCostEstinmationDocsRequest = () => ({
  type: FETCH_COST_ESTIMATION_DOCS_REQUEST,
});
export const fetchCostEstinmationDocsSuccess = data => ({
  type: FETCH_COST_ESTIMATION_DOCS_SUCCESS,
  payload: data,
});
export const fetchCostEstinmationDocsError = e => ({
  type: FETCH_COST_ESTIMATION_DOCS_ERROR,
  error: true,
  payload: e,
});
export const showListing = (listingId, config) => async (dispatch, getState, sdk) => {
  dispatch(showListingRequest());
  // Extract image configuration
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = IMAGE_VARIANTS.PREFIX,
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  try {
    // Build API parameters
    const params = {
      id: listingId,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': [
        ...IMAGE_FIELDS,
        `variants.${variantPrefix}`, // Cropped variants
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,
      ],
      ...getImageVariantConfigs(variantPrefix, aspectRatio),
    };

    const response = await sdk.listings.show(params);
    const listing = response?.data?.data;
    const listingTitle = listing?.attributes?.title;
    const authorId = listing?.relationships?.author?.data?.id?.uuid;

    if (authorId) {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(showListingSuccess(authorId, listingTitle));
      return listing;
    }
  } catch (error) {
    dispatch(showListingError(storableError(error)));
  }
};

/**
 * Since this page only needs to reload the current user,
 * we just re-export the existing fetchCurrentUser thunk.
 */

// Updated fetchCostEstimationDocuments to accept both userId and listingId
export const fetchCostEstimationDocuments = (userId, listingId, postEdit = false) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchCostEstinmationDocsRequest());
  try {
    const { statusCode, data } = await FirebaseApi.fetchCostEstimationsDocuments({
      userId,
      listingId,
      isPostEditVersion: postEdit,
    });
    if (statusCode === StatusCode.SUCCESS) {
      dispatch(fetchCostEstinmationDocsSuccess(data));
    }
  } catch (error) {
    const storable = storableError(error);
    dispatch(fetchCostEstinmationDocsError(storable));
    showToaster('error', 'Operation failed');
    throw storable;
  }
};
export const saveCostEstimation = params => async (dispatch, getState, sdk) => {
  dispatch(saveCostEstimationRequest());
  const { config, ...rest } = params;
  try {
    const { statusCode, data } = await FirebaseApi.saveCostEstimation(rest);
    if (statusCode === StatusCode.SUCCESS) {
      dispatch(saveCostEstimationSuccess(data));
      await SharetribeApi.updateListingThroughIsdk({
        id: params.listingId,
        publicData: { isPostEditVersion: true },
      });
      dispatch(showListing(params.listingId, config));
    }
  } catch (error) {
    const storable = storableError(error);
    dispatch(saveCostEstimationError(storable));
    showToaster('error', 'Operation failed');
    throw storable;
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  dispatch(setInitialValues({ costEstimationDocuments: [] }));

  try {
    const currentUser = await dispatch(fetchCurrentUser());
    const currentUserId = currentUser?.id?.uuid;
    const queryParams = new URLSearchParams(search);
    const listingId = queryParams.get(LISTING_ID);
    const postEdit = queryParams.get(POST_EDIT);
    const listing = await dispatch(showListing(new UUID(listingId), config));
    const suppliersSentEstimation = getSuppliersWhoSentEstimation(listing);
    // Take the first three supplier IDs from the supplierCostEstimation array
    const supplierIds = suppliersSentEstimation?.slice(0, 3) || [];
    // Loop over the supplier IDs and fetch cost estimation documents for each
    if (postEdit) {
      await dispatch(fetchCostEstimationDocuments(currentUserId, listingId, postEdit));
    } else {
      for (const supplierId of supplierIds) {
        await dispatch(fetchCostEstimationDocuments(supplierId, listingId, postEdit));
      }
    }

    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};
